import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/use-organization";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  CalendarClock,
  DollarSign,
  TrendingUp,
  Users,
  AlertTriangle,
  Clock,
  Package,
  ChevronRight,
  ShieldAlert,
} from "lucide-react";
import { format, addWeeks, differenceInWeeks, differenceInDays, startOfMonth, endOfMonth, isBefore, isAfter, parseISO } from "date-fns";
import { calculateSessionsRemaining } from "@/lib/package-utils";
import { useBillingModel } from "@/hooks/use-billing-model";
import type { ClientChurnRisk } from "@/hooks/use-churn-risk";

export interface ClientProjection {
  clientId: string;
  clientName: string;
  clientType: "in_person" | "virtual";
  remainingSessions: number;
  packageSize: number;
  sessionsPerWeek: number;
  projectedReupDate: Date;
  daysUntilReup: number;
  predictedPackageSize: number;
  predictedRate: number | null;
  predictedEarnings: number | null;
  confidence: "high" | "medium" | "low" | "insufficient";
  confidenceReason: string;
  packageHistory: number[];
  weeksOfData: number;
}

interface MonthlyForecast {
  month: Date;
  monthLabel: string;
  clients: ClientProjection[];
  totalEarnings: number;
  clientCount: number;
}

interface ReupProjectionsProps {
  churnRiskMap?: Map<string, ClientChurnRisk>;
  excludeAtRisk?: boolean;
}

const ReupProjections = ({ churnRiskMap, excludeAtRisk = false }: ReupProjectionsProps = {}) => {
  const { organization } = useOrganization();
  const { showReupProjections, loading: billingLoading } = useBillingModel();

  // Fetch all active clients with their packages
  const { data: clients = [], isLoading: clientsLoading } = useQuery({
    queryKey: ["reup-clients", organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];

      const { data, error } = await supabase
        .from("clients")
        .select("id, name, client_type, status, previous_session_number, current_package_size")
        .eq("organization_id", organization.id)
        .eq("status", "active");

      if (error) {
        console.error("Error fetching clients:", error);
        return [];
      }
      return data || [];
    },
    enabled: !!organization?.id,
  });

  // Fetch all packages for history analysis
  const { data: packages = [], isLoading: packagesLoading } = useQuery({
    queryKey: ["reup-packages", organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];

      const { data, error } = await supabase
        .from("packages")
        .select("id, client_id, size, rate, status, sessions_completed, created_at, package_type, duration_months")
        .eq("organization_id", organization.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching packages:", error);
        return [];
      }
      return data || [];
    },
    enabled: !!organization?.id,
  });

  // Fetch session history for usage rate calculation (last 12 weeks)
  const { data: sessions = [], isLoading: sessionsLoading } = useQuery({
    queryKey: ["reup-sessions", organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];

      // Get 12 weeks of history for accurate rate calculation
      const startDate = addWeeks(new Date(), -12);

      const { data, error } = await supabase
        .from("sessions")
        .select("id, client_id, date, cancelled, completed_at")
        .eq("organization_id", organization.id)
        .gte("date", startDate.toISOString())
        .lte("date", new Date().toISOString())
        .eq("cancelled", false);

      if (error) {
        console.error("Error fetching sessions:", error);
        return [];
      }
      return data || [];
    },
    enabled: !!organization?.id,
  });

  // Calculate projections for each client
  const projections = useMemo<ClientProjection[]>(() => {
    if (clients.length === 0) return [];

    const now = new Date();
    const results: ClientProjection[] = [];

    for (const client of clients) {
      // Get active package for this client
      const clientPackages = packages.filter(p => p.client_id === client.id);
      const activePackage = clientPackages.find(p => p.status === "active");
      
      if (!activePackage) continue; // Skip clients without active packages

      // Sessions remaining is package-scoped (not lifetime). Use the active
      // package's sessions_completed, which we already fetched at line 88.
      const packageSize = activePackage.size;
      const remainingSessions = calculateSessionsRemaining(
        packageSize,
        activePackage.sessions_completed ?? 0
      );
      
      // Get session history for this client
      const clientSessions = sessions.filter(s => s.client_id === client.id);
      
      // Calculate sessions per week
      let sessionsPerWeek = 0;
      let weeksOfData = 0;
      let confidence: "high" | "medium" | "low" | "insufficient" = "insufficient";
      let confidenceReason = "";

      if (clientSessions.length > 0) {
        // Find the date range of sessions
        const sessionDates = clientSessions.map(s => new Date(s.date)).sort((a, b) => a.getTime() - b.getTime());
        const firstSession = sessionDates[0];
        const lastSession = sessionDates[sessionDates.length - 1];
        
        // Calculate weeks from the earliest session to now (not between first and last session)
        // This prevents inflated averages when sessions are clustered
        const weeksFromFirstToNow = differenceInWeeks(now, firstSession);
        weeksOfData = Math.max(1, weeksFromFirstToNow);
        
        // For virtual clients, expected rate is ~2 sessions/month (0.5/week)
        // For in-person, varies but typically 1-3/week
        sessionsPerWeek = clientSessions.length / weeksOfData;
        
        // Determine confidence based on data quality
        if (weeksOfData >= 4 && clientSessions.length >= 4) {
          // Check consistency - calculate standard deviation of weekly sessions
          const weeklyGroups: Record<string, number> = {};
          for (const session of clientSessions) {
            const weekKey = format(new Date(session.date), "yyyy-ww");
            weeklyGroups[weekKey] = (weeklyGroups[weekKey] || 0) + 1;
          }
          const weeklyCounts = Object.values(weeklyGroups);
          const avg = weeklyCounts.reduce((a, b) => a + b, 0) / weeklyCounts.length;
          const variance = weeklyCounts.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) / weeklyCounts.length;
          const stdDev = Math.sqrt(variance);
          const coeffOfVariation = avg > 0 ? stdDev / avg : 1;
          
          if (coeffOfVariation < 0.3) {
            confidence = "high";
            confidenceReason = "Consistent schedule over 4+ weeks";
          } else if (coeffOfVariation < 0.6) {
            confidence = "medium";
            confidenceReason = "Some schedule variability";
          } else {
            confidence = "low";
            confidenceReason = "Irregular schedule pattern";
          }
        } else if (weeksOfData >= 2 && clientSessions.length >= 2) {
          confidence = "medium";
          confidenceReason = `Based on ${weeksOfData} weeks of data`;
        } else {
          confidence = "low";
          confidenceReason = "Limited session history";
        }
      } else {
        // No session data - use defaults based on client type
        if (client.client_type === "virtual") {
          sessionsPerWeek = 0.5; // ~2 per month for virtual
          confidenceReason = "Using virtual client default (2/month)";
        } else {
          sessionsPerWeek = 1; // Default assumption
          confidenceReason = "No session history - using default";
        }
        confidence = "insufficient";
      }

      // Project re-up date
      let projectedReupDate: Date;
      let daysUntilReup: number;
      
      if (remainingSessions <= 0) {
        projectedReupDate = now;
        daysUntilReup = 0;
      } else if (sessionsPerWeek > 0) {
        const weeksUntilReup = remainingSessions / sessionsPerWeek;
        projectedReupDate = addWeeks(now, weeksUntilReup);
        daysUntilReup = differenceInDays(projectedReupDate, now);
      } else {
        // No sessions happening, project far out
        projectedReupDate = addWeeks(now, 52);
        daysUntilReup = 365;
        confidence = "low";
        confidenceReason = "Client not currently training";
      }

      // Predict package size based on history
      const completedPackages = clientPackages
        .filter(p => p.status === "completed" || p.status === "active")
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      
      const packageHistory = completedPackages.map(p => p.size);
      
      let predictedPackageSize: number;
      if (packageHistory.length >= 2) {
        // Use most recent package size (clients tend to stick with what works)
        predictedPackageSize = packageHistory[0];
      } else if (packageHistory.length === 1) {
        predictedPackageSize = packageHistory[0];
      } else {
        // Default based on client type
        predictedPackageSize = client.client_type === "virtual" ? 12 : 20;
      }

      // Get rate from most recent package with a rate
      const packageWithRate = clientPackages.find(p => p.rate != null);
      const predictedRate = packageWithRate ? Number(packageWithRate.rate) : null;
      const predictedEarnings = predictedRate ? predictedPackageSize * predictedRate : null;

      results.push({
        clientId: client.id,
        clientName: client.name,
        clientType: client.client_type as "in_person" | "virtual",
        remainingSessions,
        packageSize: activePackage.size,
        sessionsPerWeek,
        projectedReupDate,
        daysUntilReup,
        predictedPackageSize,
        predictedRate,
        predictedEarnings,
        confidence,
        confidenceReason,
        packageHistory,
        weeksOfData,
      });
    }

    // Sort by projected re-up date (soonest first)
    return results.sort((a, b) => a.daysUntilReup - b.daysUntilReup);
  }, [clients, packages, sessions]);

  // Apply at-risk filter when conservative mode is enabled
  const filteredProjections = useMemo(() => {
    if (!excludeAtRisk || !churnRiskMap) return projections;
    return projections.filter(p => {
      const risk = churnRiskMap.get(p.clientId);
      return !risk || (risk.riskScore.level !== 'high' && risk.riskScore.level !== 'critical');
    });
  }, [projections, excludeAtRisk, churnRiskMap]);

  // Group projections by month for forecast
  const monthlyForecasts = useMemo<MonthlyForecast[]>(() => {
    const now = new Date();
    const forecasts: MonthlyForecast[] = [];

    // Create 6 months of forecasts
    for (let i = 0; i < 6; i++) {
      const month = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const monthStart = startOfMonth(month);
      const monthEnd = endOfMonth(month);

      const clientsInMonth = filteredProjections.filter(p => {
        const reupDate = p.projectedReupDate;
        return (isAfter(reupDate, monthStart) || format(reupDate, "yyyy-MM") === format(monthStart, "yyyy-MM")) 
          && isBefore(reupDate, monthEnd);
      });
      
      const totalEarnings = clientsInMonth.reduce((sum, c) => sum + (c.predictedEarnings || 0), 0);
      
      forecasts.push({
        month,
        monthLabel: format(month, "MMM yyyy"),
        clients: clientsInMonth,
        totalEarnings,
        clientCount: clientsInMonth.length,
      });
    }
    
    return forecasts;
  }, [filteredProjections]);

  // Summary stats
  const stats = useMemo(() => {
    const dueNow = filteredProjections.filter(p => p.daysUntilReup <= 0).length;
    const dueSoon = filteredProjections.filter(p => p.daysUntilReup > 0 && p.daysUntilReup <= 14).length;
    const dueThisMonth = filteredProjections.filter(p => {
      const now = new Date();
      return p.projectedReupDate.getMonth() === now.getMonth() && 
             p.projectedReupDate.getFullYear() === now.getFullYear();
    }).length;
    
    const thisMonthEarnings = monthlyForecasts[0]?.totalEarnings || 0;
    const nextMonthEarnings = monthlyForecasts[1]?.totalEarnings || 0;
    const totalProjectedEarnings = monthlyForecasts.reduce((sum, m) => sum + m.totalEarnings, 0);
    
    const highConfidenceCount = filteredProjections.filter(p => p.confidence === "high").length;
    const avgConfidencePercent = filteredProjections.length > 0
      ? Math.round((highConfidenceCount / filteredProjections.length) * 100)
      : 0;

    return {
      dueNow,
      dueSoon,
      dueThisMonth,
      thisMonthEarnings,
      nextMonthEarnings,
      totalProjectedEarnings,
      totalClients: filteredProjections.length,
      avgConfidencePercent,
    };
  }, [filteredProjections, monthlyForecasts]);

  // Get confidence badge variant
  const getConfidenceBadge = (confidence: string) => {
    switch (confidence) {
      case "high": return { variant: "default" as const, label: "High confidence" };
      case "medium": return { variant: "secondary" as const, label: "Medium confidence" };
      case "low": return { variant: "outline" as const, label: "Low confidence" };
      default: return { variant: "outline" as const, label: "Needs data" };
    }
  };

  // Format days until re-up
  const formatDaysUntil = (days: number) => {
    if (days <= 0) return "Due now";
    if (days === 1) return "Tomorrow";
    if (days < 7) return `${days} days`;
    if (days < 14) return "~1 week";
    if (days < 21) return "~2 weeks";
    if (days < 35) return "~1 month";
    if (days < 60) return "~6 weeks";
    if (days < 90) return "~2 months";
    return `~${Math.round(days / 30)} months`;
  };

  const isLoading = clientsLoading || packagesLoading || sessionsLoading || billingLoading;

  // Re-up projections only apply to package-based billing models
  if (!billingLoading && !showReupProjections) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Calculating projections...</div>
      </div>
    );
  }

  if (projections.length === 0) {
    return (
      <Card className="glass-card border-0">
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">No active clients with packages found.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Add clients with active packages to see re-up projections.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="glass-card border-0">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.dueNow + stats.dueSoon}</p>
                <p className="text-xs text-muted-foreground">Due Now/Soon</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-0">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-warning/10 flex items-center justify-center">
                <CalendarClock className="h-5 w-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.dueThisMonth}</p>
                <p className="text-xs text-muted-foreground">Due This Month</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-0">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">${stats.thisMonthEarnings.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Projected This Month</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-0">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-success/10 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold">${stats.nextMonthEarnings.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Projected Next Month</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Forecast */}
      <Card className="glass-card border-0">
        <CardHeader>
          <CardTitle className="text-lg">6-Month Re-up Forecast</CardTitle>
          <CardDescription>
            Projected earnings from client re-ups based on usage patterns and package history
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {monthlyForecasts.map((forecast, index) => {
              const maxEarnings = Math.max(...monthlyForecasts.map(f => f.totalEarnings), 1);
              const barPercent = (forecast.totalEarnings / maxEarnings) * 100;
              const isCurrentMonth = index === 0;
              
              return (
                <div key={forecast.monthLabel} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className={isCurrentMonth ? "font-semibold" : ""}>
                      {forecast.monthLabel}
                      {isCurrentMonth && <span className="text-muted-foreground ml-2">(current)</span>}
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground">
                        {forecast.clientCount} client{forecast.clientCount !== 1 ? "s" : ""}
                      </span>
                      <span className={`font-semibold ${forecast.totalEarnings > 0 ? "text-primary" : "text-muted-foreground"}`}>
                        ${forecast.totalEarnings.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <Progress 
                    value={barPercent} 
                    className="h-2"
                  />
                </div>
              );
            })}
          </div>
          
          <div className="mt-4 pt-4 border-t border-border/50 flex justify-between items-center">
            <span className="text-sm text-muted-foreground">6-month total projected</span>
            <span className="text-lg font-bold text-primary">
              ${stats.totalProjectedEarnings.toLocaleString()}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Client-by-Client Projections */}
      <Card className="glass-card border-0">
        <CardHeader>
          <CardTitle className="text-lg">Client Re-up Timeline</CardTitle>
          <CardDescription>
            Individual projections sorted by expected re-up date. Predictions are based on each client's 
            session frequency and package purchase history.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredProjections.map((projection) => {
              const confidenceBadge = getConfidenceBadge(projection.confidence);
              const isDueNow = projection.daysUntilReup <= 0;
              const isDueSoon = projection.daysUntilReup > 0 && projection.daysUntilReup <= 14;
              const churnRisk = churnRiskMap?.get(projection.clientId);
              const isHighRisk = churnRisk && (churnRisk.riskScore.level === 'high' || churnRisk.riskScore.level === 'critical');

              return (
                <div
                  key={projection.clientId}
                  className={`p-4 rounded-lg border ${
                    isDueNow
                      ? "border-destructive/50 bg-destructive/5"
                      : isDueSoon
                        ? "border-warning/50 bg-warning/5"
                        : "border-border/50 bg-card/50"
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{projection.clientName}</span>
                        <Badge variant={projection.clientType === "virtual" ? "secondary" : "outline"} className="text-xs">
                          {projection.clientType === "virtual" ? "Virtual" : "In-person"}
                        </Badge>
                        <Badge variant={confidenceBadge.variant} className="text-xs">
                          {confidenceBadge.label}
                        </Badge>
                        {isDueNow && (
                          <Badge variant="destructive" className="text-xs">
                            <ShieldAlert className="h-3 w-3 mr-1" />
                            Overdue
                          </Badge>
                        )}
                        {!isDueNow && isDueSoon && (
                          <Badge variant="warning">
                            Due soon
                          </Badge>
                        )}
                        {isHighRisk && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {churnRisk.riskScore.level === 'critical' ? 'Churn risk: Critical' : 'Churn risk: High'}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-sm">
                        <div>
                          <span className="text-muted-foreground">Remaining: </span>
                          <span className="font-medium">{projection.remainingSessions}/{projection.packageSize}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Rate: </span>
                          <span className="font-medium">
                            {projection.sessionsPerWeek > 0 
                              ? `${projection.sessionsPerWeek.toFixed(1)}/week` 
                              : "No data"}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Predicted size: </span>
                          <span className="font-medium">{projection.predictedPackageSize} sessions</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Predicted rate: </span>
                          <span className="font-medium">
                            {projection.predictedRate 
                              ? `$${projection.predictedRate}/session` 
                              : "Not set"}
                          </span>
                        </div>
                      </div>
                      
                      <p className="text-xs text-muted-foreground">
                        {projection.confidenceReason}
                        {projection.packageHistory.length > 1 && (
                          <span className="ml-1">
                            • Past packages: {projection.packageHistory.slice(0, 3).join(", ")} sessions
                          </span>
                        )}
                        {isHighRisk && churnRisk && (
                          <span className="ml-1 text-destructive">
                            • {churnRisk.riskScore.primaryReason}
                          </span>
                        )}
                      </p>
                    </div>

                    <div className="text-right flex-shrink-0">
                      <p className={`text-lg font-bold ${isDueNow ? "text-destructive" : isDueSoon ? "text-warning" : "text-foreground"}`}>
                        {formatDaysUntil(projection.daysUntilReup)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {projection.daysUntilReup > 0 && format(projection.projectedReupDate, "MMM d, yyyy")}
                      </p>
                      {projection.predictedEarnings && (
                        <p className="text-sm font-semibold text-primary mt-1">
                          ${projection.predictedEarnings.toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Methodology Note */}
      <Card className="glass-card border-0 border-l-4 border-l-muted">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-xl bg-muted/50 flex items-center justify-center flex-shrink-0">
              <Clock className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">How projections work</p>
              <p>
                Re-up dates are calculated from each client's <strong>average sessions per week</strong> (based on 12 weeks of history) 
                divided into their <strong>remaining sessions</strong>. Package size predictions use the client's most recent 
                package purchase. Confidence reflects data quality—clients with 4+ weeks of consistent training have high confidence.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReupProjections;
